// LLMAdviceTab.jsx
// DAS-2897: the "LLM Advice" tab in the Alerts modal — the LLM Portfolio
// Analysis's concrete instructions (BUY/SELL/SWAP with amounts), newest first.
// Deliberately its own tab, never rows in the Portfolio Alerts list: advice is
// not a clause alert (owner rule), and its record stays under its own label
// (DAS-2855 separation). Outcome measurement is DAS-2898.
//
// Reads GET /api/llm_advice_list/<userId>; Seen / Followed-up ride
// POST /api/llm_advice_check_update (manual tick records source='manual' so the
// DAS-2898 auto-detector never overwrites a human mark).

import React, { useEffect, useState, useCallback } from 'react';
import { Card, Tag, Typography, Spin, Alert, Checkbox, Empty, message, Modal, Input, Button } from 'antd';
import CompactAlertCard from './CompactAlertCard'; // DAS-2897: in-place alert card modal
import ThreeTablesModal from './SearchResultModal'; // DAS-2899: clickable tickers
import LLMAnalysisBody from './LLMAnalysisBody'; // DAS-2904: final advice first, debate folded
import LLMCostLine, { costFromResponse } from './LLMCostLine'; // DAS-3044: the run's cost line, also in the full analysis
import { useSelector } from 'react-redux';
import api from '../functions/api';
import getUserId from '../functions/getUserId';
import findFollowedUpAlert from '../functions/findFollowedUpAlert'; // DAS-3189

const { Text, Paragraph } = Typography;

const ACTION_COLOR = { BUY: 'green', SELL: 'volcano', SWAP: 'geekblue', HOLD: 'default' };
// DAS-3006: one advice row per question that passed; a blocked row is a candidate, not an action
// DAS-3228: written out — the bare 'risk' next to the horizon 'long' read as "long risk"
const LANE_LABEL = { risk: 'question: biggest risk', strategy: 'question: strategic situation',
  cash: 'question: cash surplus', swap: 'question: sell or swap' };
const LANE_QUESTION = {   // advice_lanes.LANE_QUESTIONS
  risk: '1. What is the biggest risk? Is it big enough to remediate with transactions?',
  strategy: '2. From a strategic perspective, is there a compelling situation?',
  cash: '3. What is the biggest opportunity for applying the cash surplus?',
  swap: '4. Is there a holding I should sell or swap?',
};
const laneChip = (a) => (a.lane && a.lane !== 'main'
  ? <Tag data-testid="das3006-lane" title={LANE_QUESTION[a.lane] || ''} style={{ fontSize: 11 }}>{LANE_LABEL[a.lane] || a.lane}</Tag> : null);
// DAS-3228: the stored horizon is 'short' / 'long'; the owner reads 'short term' / 'long term'
const horizonText = (h) => (h === 'short' ? 'short term' : h === 'long' ? 'long term' : h);
// DAS-3226: the account an advice row acts in (Portfolio_LLM_Advice.account), in the owner's names
// DAS-3366: 'book' (whole portfolio) added for the per-account block's fourth line
const ACCOUNT_LABEL = { WenO: 'W&O', saxo: 'Saxo', degiro: 'DeGiro main', degiro_rebalance: 'DeGiro Rebalance', book: 'Whole portfolio' };
const accountChip = (a) => (a && a.account
  ? <Tag color="blue" data-testid="das3226-account" style={{ fontSize: 11 }}>{ACCOUNT_LABEL[a.account] || a.account}</Tag> : null);

// DAS-3366: the four "Per account" block lanes, fixed order, and the ACCOUNT_LABEL key each maps to
// (lane names and Portfolio_LLM_Advice.account values differ, so they need their own mapping).
const BLOCK_LANES = ['acct-saxo', 'acct-degiro', 'acct-rebalance', 'book'];
const LANE_ACCOUNT_KEY = { 'acct-saxo': 'saxo', 'acct-degiro': 'degiro', 'acct-rebalance': 'degiro_rebalance', book: 'book' };
const blockLineLabel = (a) => ACCOUNT_LABEL[LANE_ACCOUNT_KEY[a.lane]] || a.lane;
const accountStatusText = (a) => (a && a.account_status) || 'not measured';
// the move in words — same action fields as every other row, read for one account's line
const accountMoveText = (a) => {
  const act = (a?.action || '').toUpperCase();
  const amt = a?.amount_eur != null ? ` ${fmtAmount(a.amount_eur)}` : '';
  if (act === 'SWAP') return `sell ${a.sell_ticker}, buy ${a.buy_ticker}${amt}`;
  if (act === 'SELL') return `sell ${a.sell_ticker}${amt}`;
  if (act === 'BUY') return `buy ${a.buy_ticker}${amt}`;
  return 'nothing today';
};
// "proposed today" when there is a move and nothing blocks it; otherwise "not today: <reason>"
const accountTimingText = (a) => {
  const act = (a?.action || '').toUpperCase();
  const hasMove = act === 'BUY' || act === 'SELL' || act === 'SWAP';
  if (hasMove && !a?.blocked_by) return 'proposed today';
  return `not today: ${a?.blocked_by || a?.rationale_short || 'nothing to do today'}`;
};

// DAS-3365 stores a block row's words as "<Status>. Move: <move>. Today: <when>. <reason>". The
// status already shows as a tag, so a line without a live trade reads the rest of that sentence:
// a "not today" move is HOLD without tickers, and only its words still name the move.
const storedAccountWords = (a) => {
  const act = (a?.action || '').toUpperCase();
  const live = (act === 'BUY' || act === 'SELL' || act === 'SWAP') && !a?.blocked_by;
  const t = a?.rationale_short || '';
  const i = t.indexOf('Move:');
  return !live && i >= 0 ? t.slice(i) : null;
};

// DAS-3366: group the block-lane rows by run_date (fixed BLOCK_LANES order within a day), and
// index every PROPOSED trade (sell+buy ticker pair) to its account label, so a question/re-pick
// row with the same trade can render "see <account>" instead of repeating it.
const buildAccountBlocks = (rows) => {
  const byDate = new Map();
  rows.forEach((a) => {
    if (!BLOCK_LANES.includes(a.lane)) return;
    if (!byDate.has(a.run_date)) byDate.set(a.run_date, { lines: [], tradeKeyToLabel: new Map() });
    const entry = byDate.get(a.run_date);
    entry.lines.push(a);
    const act = (a.action || '').toUpperCase();
    const hasMove = act === 'BUY' || act === 'SELL' || act === 'SWAP';
    if (hasMove && !a.blocked_by) {
      entry.tradeKeyToLabel.set(`${a.sell_ticker || ''}|${a.buy_ticker || ''}`, blockLineLabel(a));
    }
  });
  byDate.forEach((entry) => entry.lines.sort((x, y) => BLOCK_LANES.indexOf(x.lane) - BLOCK_LANES.indexOf(y.lane)));
  return byDate;
};
// DAS-3022: the two hard rules code enforces read as sentences, not codes
// DAS-3055: how many days a carried-over sell has been standing (run_date − standing_since + 1)
const standingDays = (a) => {
  if (!a || !a.standing_since || !a.run_date) return null;
  const d = Math.round((new Date(a.run_date) - new Date(a.standing_since)) / 86400000) + 1;
  return d > 0 ? d : 1;
};
const standingChip = (a) => (a && a.carried_over
  ? <Tag color="gold" data-testid="das3055-standing-chip" title={`unchanged since ${a.standing_since || '?'}`} style={{ fontSize: 11 }}>
      day {standingDays(a) ?? '?'}
    </Tag> : null);

const blockedLabel = (b) => {
  const s = String(b || '');
  if (s.startsWith('panic day')) return 'not today — panic day, your rule of 12 September';
  if (s.startsWith('trim to ETF')) return 'candidate — a trimmed winner goes into an ETF, not another stock';
  return `candidate — blocked by ${s}`;
};
const blockedChip = (a) => (a.blocked_by
  ? <Tag color="default" data-testid="das3006-blocked" title={a.blocked_by} style={{ fontSize: 11, color: '#999' }}>{blockedLabel(a.blocked_by)}</Tag> : null);

// DAS-2964: the fixed act-by meaning of each urgency tag — same dictionary the
// debate prompts use (VALID_URGENCY in portfolio_advice_record.py).
const URGENCY_MEANING = {
  'Very Urgent': 'act today',
  'Urgent': 'act this week',
  'Mild Urgent': 'act within two weeks',
  'Light Urgent': 'when convenient',
};

const fmtAmount = (v) => (v == null ? '' : `~€${Math.round(v).toLocaleString()}`);

// DAS-2898: the verdict at the LONGEST matured horizon (6m > 1m > 1w).
// Positive edge = the advice was right (sell edges are direction-applied).
const verdictLine = (a) => {
  const pick = a?.outcome_ae_sp_6m != null ? ['6 months on', a.outcome_ae_sp_6m]
    : a?.outcome_1m_ae_sp != null ? ['1 month on', a.outcome_1m_ae_sp]
    : a?.outcome_1w_ae_sp != null ? ['1 week on', a.outcome_1w_ae_sp]
    : null;
  if (!pick) return null;
  const [label, edge] = pick;
  const right = edge > 0;
  const vs = (a.action || '').toUpperCase() === 'SWAP' ? 'buy leg vs sell leg' : 'vs the S&P 500';
  return { text: `${label}: ${edge > 0 ? '+' : ''}${edge.toFixed(1)}% ${vs} — ${right ? 'so far right' : 'so far wrong'}`, right };
};

// DAS-2899: every ticker is clickable and opens the standard search result,
// like on the other tabs. A swap renders two independently clickable tickers.
// DAS-3366: a question/re-pick row whose trade is already shown on an account's
// block line (seeAccount set) points there instead of repeating the trade.
const tickerNodes = (a, onTicker, seeAccount) => {
  if (seeAccount) return <span data-testid="das3366-see-account">see {seeAccount}</span>;
  const act = (a?.action || '').toUpperCase();
  const T = ({ t }) => t ? (
    <span data-testid="das2899-ticker" style={{ color: '#1677ff', cursor: 'pointer' }}
          onClick={() => onTicker(t)}>{t}</span>
  ) : <span>?</span>;
  if (act === 'SWAP') return <span><T t={a?.sell_ticker} /> → <T t={a?.buy_ticker} /></span>;
  if (act === 'SELL') return <T t={a?.sell_ticker} />;
  // DAS-2986: HOLD is a real answer — "no trade today", the rationale says what to do instead
  if (act === 'HOLD' && !a?.buy_ticker) return <span data-testid="das2986-hold">no trade today</span>;
  return <T t={a?.buy_ticker} />;
};

const LLMAdviceTab = ({ onUnseenChange }) => {  // DAS-2939: tab tells its badge
  const authUserId = useSelector(state => state?.auth?.userId);
  const userId = authUserId || getUserId();
  // DAS-2897 owner feedback: both drill-downs open IN PLACE as modals — close
  // and you are back on this tab, never parked on another tab.
  const pfAlerts = useSelector(state => state?.portfolio?.data?.pf_alert);
  // DAS-3189: the followed-up alert can be a FAVORITES alert (e.g. a "Step In" on a stock
  // you watch but do not hold) — searching only the holdings' list called it "archived".
  const favAlerts = useSelector(state => state?.favorites?.data?.favorites_alert_data);
  // DAS-3190: ... or an OPPORTUNITIES alert (a stock you neither hold nor watch).
  const oppAlerts = useSelector(state => state?.portfolio?.data?.opportunities_alerts);
  const alertEffectiveness = useSelector(state => state?.portfolio?.data?.alert_effectiveness_data);
  const [alertModal, setAlertModal] = useState(null);        // the alert card
  const [searchTicker, setSearchTicker] = useState(null);    // DAS-2899 search result
  const [analysisText, setAnalysisText] = useState(null);    // the full reasoning
  const [analysisCost, setAnalysisCost] = useState(null);    // DAS-3044: the run's cost line
  const [analysisOpen, setAnalysisOpen] = useState(false);

  const openAlertCard = (id) => {
    const hit = findFollowedUpAlert(id, pfAlerts, favAlerts, oppAlerts);
    if (hit) setAlertModal(hit);
    else message.info(`Alert #${id} is no longer open in Portfolio, Favorites or Opportunities alerts.`);
  };

  const openFullAnalysis = async () => {
    setAnalysisOpen(true);
    if (analysisText == null) {
      try {
        const res = await api.get(`/portfolio_llm_analysis/${userId}`);
        setAnalysisText(res.data?.LLM_analysis || 'No analysis stored.');
        setAnalysisCost(costFromResponse(res.data));
      } catch (err) {
        setAnalysisText('Could not load the analysis: ' + (err?.response?.data?.message || err.message));
      }
    }
  };

  const [advice, setAdvice] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    if (!userId) return;
    api.get(`/llm_advice_list/${userId}`)
      .then(res => {
        const list = res.data?.advice || [];
        setAdvice(list);
        // DAS-2939: every (re)load — incl. after seen/followed-up saves — updates the tab dot
        if (onUnseenChange) onUnseenChange(list.some(a => !a.seen));
      })
      .catch(err => setError(err?.response?.data?.error || err.message));
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  // DAS-2949: create a Jira ticket from an advice — same backend as the
  // portfolio-alert flow (DAS-2463): duplicate check by ticker, then
  // /jira/create_ticket with an advice-shaped summary/description.
  const [jiraAdvice, setJiraAdvice] = useState(null);   // advice being ticketed; null = closed
  const [jiraNotes, setJiraNotes] = useState('');
  const [jiraCreating, setJiraCreating] = useState(false);
  const [jiraDuplicates, setJiraDuplicates] = useState(null);

  const openJiraModal = async (a) => {
    setJiraNotes('');
    setJiraDuplicates(null);
    setJiraAdvice(a);
    const ticker = a.buy_ticker || a.sell_ticker;
    if (!ticker) { setJiraDuplicates([]); return; }
    try {
      const res = await api.post('/jira/search_alert_tickets', { ticker });
      setJiraDuplicates(res.data?.tickets || []);
    } catch {
      setJiraDuplicates([]); // on error, proceed without duplicate check
    }
  };

  const submitJira = async () => {
    const a = jiraAdvice;
    if (!a) return;
    setJiraCreating(true);
    try {
      const tickers = [a.sell_ticker && `sell ${a.sell_ticker}`, a.buy_ticker && `buy ${a.buy_ticker}`]
        .filter(Boolean).join(', ') || 'no ticker';
      const summary = `LLM Advice ${a.run_date}: ${(a.action || '').toUpperCase()} — ${tickers}`;
      const description = [
        `## LLM Advice (${a.run_date})`,
        `- **Action:** ${(a.action || '').toUpperCase()}`,
        a.sell_ticker ? `- **Sell:** ${a.sell_ticker}` : '',
        a.buy_ticker ? `- **Buy:** ${a.buy_ticker}` : '',
        a.amount_eur != null ? `- **Amount:** €${Number(a.amount_eur).toLocaleString()}` : '',
        a.urgency ? `- **Urgency:** ${a.urgency}${URGENCY_MEANING[a.urgency] ? ` (${URGENCY_MEANING[a.urgency]})` : ''}` : '',
        a.horizon ? `- **Horizon:** ${a.horizon === 'short' ? 'short term' : 'long term'}` : '',
        a.followed_up_alert_id != null ? `- **Follows up alert:** #${a.followed_up_alert_id}` : '',
        '',
        `## Rationale`,
        a.rationale_short || '(none stored)',
        '',
        jiraNotes ? `## User Notes\n${jiraNotes}` : '',
      ].filter(Boolean).join('\n');
      const res = await api.post('/jira/create_ticket', {
        summary,
        description,
        issue_type: 'Task',
        priority: 'Medium',
        labels: ['alert-action', 'llm-advice'],
      });
      if (res.data?.key) {
        message.success(
          <span>
            Created <a href={res.data.url} target="_blank" rel="noopener noreferrer">{res.data.key}</a>
          </span>
        );
        setJiraAdvice(null);
      }
    } catch (err) {
      message.error(err.response?.data?.error || err.message || 'Failed to create ticket');
    } finally {
      setJiraCreating(false);
    }
  };

  const jiraLink = (a) => (
    <Text
      data-testid="das2949-jira-link"
      style={{ fontSize: 12, color: '#1677ff', cursor: 'pointer' }}
      onClick={() => openJiraModal(a)}
    >
      Jira ticket
    </Text>
  );

  const update = async (id, fields) => {
    try {
      await api.post('/llm_advice_check_update', { id, ...fields });
      load();
    } catch (err) {
      message.error('Update failed: ' + (err?.response?.data?.message || err.message));
    }
  };

  if (error) return <Alert type="error" message="Could not load LLM advice" description={error} />;
  if (advice === null) return <Spin style={{ display: 'block', margin: '40px auto' }} />;
  if (!advice.length) {
    return <Empty description="No LLM Portfolio Analysis advice yet — advice appears here after each analysis run." />;
  }

  // DAS-3366: block-lane rows (acct-saxo / acct-degiro / acct-rebalance / book) render inside
  // the "Per account" block, not as a normal row; every other lane is unaffected (unchanged).
  const dayBlocks = buildAccountBlocks(advice);
  const normalRows = advice.filter(a => !BLOCK_LANES.includes(a.lane));
  const [latest, ...history] = normalRows.length ? normalRows : advice;

  const seeAccountFor = (a) => {
    if (BLOCK_LANES.includes(a.lane)) return null;
    if (!a.sell_ticker && !a.buy_ticker) return null;
    const entry = dayBlocks.get(a.run_date);
    return entry ? entry.tradeKeyToLabel.get(`${a.sell_ticker || ''}|${a.buy_ticker || ''}`) || null : null;
  };

  const statusBoxes = (a) => (
    <span style={{ display: 'inline-flex', gap: 16 }}>
      <Checkbox checked={!!a.seen} onChange={e => update(a.ID, { seen: e.target.checked ? 1 : 0 })}>Seen</Checkbox>
      <Checkbox checked={!!a.followed_up} onChange={e => update(a.ID, e.target.checked
        ? { followed_up: 1, seen: 1 }   // following implies having seen it
        : { followed_up: 0 })}>Followed up</Checkbox>
      {!a.followed_up && a.followup_proposal_status === 'proposed' && (
        <Tag data-testid="das2898-followup-proposed" color="gold" title={a.followup_proposal_evidence || ''}>
          trade detected — followed up?
        </Tag>
      )}
      {verdictLine(a) && (
        <Text data-testid="das2898-verdict" style={{ fontSize: 12, color: verdictLine(a).right ? '#2c7a44' : '#cf1322' }}>
          {verdictLine(a).text}
        </Text>
      )}
    </span>
  );

  // DAS-3366: one "Per account" block per day, one line per block lane, each with its own
  // Seen / Followed up boxes (statusBoxes — same PATCH per row ID as every other row).
  const renderAccountBlock = (date, entry) => (
    <Card key={`das3356-block-${date}`} size="small" data-testid="das3356-account-block"
          style={{ marginBottom: 10, background: '#fafafa', borderColor: '#d9d9d9' }}>
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>Per account — {date}</Text>
      {entry.lines.map(a => (
        <div key={a.ID} data-testid={`das3356-account-line-${a.lane}`}
             style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '4px 0' }}>
          <Text strong style={{ minWidth: 130 }}>{blockLineLabel(a)}</Text>
          <Tag>{accountStatusText(a)}</Tag>
          {storedAccountWords(a) ? <Text>{storedAccountWords(a)}</Text> : (
            <>
              <Text>{accountMoveText(a)}</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>{accountTimingText(a)}</Text>
            </>
          )}
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>{statusBoxes(a)}</span>
        </div>
      ))}
    </Card>
  );

  // DAS-3366: the block for latest.run_date is shown once, above the top Card — never again
  // in the history list, even if another history row happens to share that date.
  const shownBlockDates = new Set([latest.run_date]);

  return (
    <div data-testid="das2897-llm-advice-tab">
      <Paragraph type="secondary" style={{ marginBottom: 12 }}>
        The concrete instructions from the LLM Portfolio Analysis — kept apart from your clause
        alerts on purpose, so each keeps its own track record. The full reasoning behind each
        advice lives in the LLM Portfolio Analysis panel on the dashboard.
      </Paragraph>

      {dayBlocks.has(latest.run_date) && renderAccountBlock(latest.run_date, dayBlocks.get(latest.run_date))}

      <Card size="small" style={{ marginBottom: 14, borderColor: '#b7c8e8' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Tag color={ACTION_COLOR[(latest.action || '').toUpperCase()] || 'default'}>{latest.action}</Tag>
          <Text strong>{tickerNodes(latest, setSearchTicker, seeAccountFor(latest))}</Text>
          <Text>{fmtAmount(latest.amount_eur)}</Text>
          {/* DAS-2989: the advice's numbers (cash, amounts) are as of the run time — the brokers refresh later */}
          {latest.run_epoch && (
            <Text type="secondary" style={{ fontSize: 12 }} data-testid="das2989-run-time"
                  title="Cash and amounts in this advice are as of this time; the brokers may have refreshed since">
              as of {new Date(latest.run_epoch * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </Text>
          )}
          {latest.urgency && (
            <Tag color="orange" data-testid="das2964-urgency">
              {latest.urgency}
              {URGENCY_MEANING[latest.urgency] ? ` — ${URGENCY_MEANING[latest.urgency]}` : ''}
            </Tag>
          )}
          {latest.horizon && <Text type="secondary" data-testid="das3228-horizon" style={{ fontSize: 12 }}>{horizonText(latest.horizon)}</Text>}
          {accountChip(latest)}{laneChip(latest)}{blockedChip(latest)}
          <Text type="secondary" style={{ marginLeft: 'auto', fontSize: 12 }}>{latest.run_date}</Text>
        </div>
        {/* DAS-2973: the deterministic trade check failed — show why, loudly */}
        {latest.check_warnings && (
          <div data-testid="das2973-check-warning"
               style={{ margin: '8px 0', padding: '6px 10px', background: '#fff1f0',
                        border: '1px solid #ffa39e', borderRadius: 6, fontSize: 13 }}>
            ⚠ <b>CHECK FAILED:</b> {latest.check_warnings}
          </div>
        )}
        {/* DAS-3055: a sell that stands unfollowed is shown as one line with its day count, every day */}
        {latest.carried_over ? (
          <div data-testid="das3055-standing"
               style={{ margin: '8px 0', padding: '6px 10px', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6, fontSize: 13 }}>
            ⏳ <b>Standing, not followed:</b> {latest.rationale_short}
            {latest.standing_since ? ` — first recommended ${latest.standing_since}, day ${standingDays(latest)}` : ''}
          </div>
        ) : (
          latest.rationale_short && <Paragraph style={{ margin: '8px 0' }}>{latest.rationale_short}</Paragraph>
        )}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          {statusBoxes(latest)}
          {latest.followed_up_alert_id && (
            <Text
              data-testid="das2897-followup-link"
              style={{ fontSize: 12, color: '#1677ff', cursor: 'pointer' }}
              onClick={() => openAlertCard(latest.followed_up_alert_id)}
            >
              follows up alert #{latest.followed_up_alert_id}
            </Text>
          )}
          <Text
            data-testid="das2897-full-analysis-link"
            style={{ fontSize: 12, color: '#1677ff', cursor: 'pointer' }}
            onClick={openFullAnalysis}
          >
            full analysis
          </Text>
          {jiraLink(latest)}
          {latest.agrees_with_swap_engine != null && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              swap engine: {latest.agrees_with_swap_engine ? 'agrees' : 'proposed nothing / differs'}
            </Text>
          )}
        </div>
      </Card>

      {history.map(a => {
        const showBlock = dayBlocks.has(a.run_date) && !shownBlockDates.has(a.run_date);
        if (showBlock) shownBlockDates.add(a.run_date);
        return (
          <React.Fragment key={a.ID}>
            {showBlock && renderAccountBlock(a.run_date, dayBlocks.get(a.run_date))}
            <div style={{ padding: '6px 4px', borderBottom: '1px solid #f0f0f0', opacity: a.blocked_by ? 0.55 : 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <Text type="secondary" style={{ fontSize: 12, width: 80 }}>{a.run_date}</Text>
                <Tag color={ACTION_COLOR[(a.action || '').toUpperCase()] || 'default'} style={{ width: 54, textAlign: 'center' }}>{a.action}</Tag>
                <Text style={{ minWidth: 110 }}>{tickerNodes(a, setSearchTicker, seeAccountFor(a))}</Text>
                {accountChip(a)}{laneChip(a)}{blockedChip(a)}{standingChip(a)}
                <Text type="secondary" style={{ fontSize: 12, minWidth: 70 }}>{fmtAmount(a.amount_eur)}</Text>
                <span style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>{jiraLink(a)}{statusBoxes(a)}</span>
              </div>
              {verdictLine(a) && (
                <div data-testid="das2898-verdict-row" style={{ fontSize: 12, marginTop: 2, paddingLeft: 90, color: verdictLine(a).right ? '#2c7a44' : '#cf1322' }}>
                  {verdictLine(a).text}
                </div>
              )}
            </div>
          </React.Fragment>
        );
      })}

      {searchTicker && (
        <ThreeTablesModal
          tickerValue={searchTicker}
          open={!!searchTicker}
          onClose={() => setSearchTicker(null)}
          userId={userId}
        />
      )}

      <Modal open={!!alertModal} onCancel={() => setAlertModal(null)} footer={null} width={760} destroyOnClose>
        {alertModal && (
          <div data-testid="das3190-followed-alert-card">
            <CompactAlertCard alert={alertModal} alertEffectiveness={alertEffectiveness} onClose={() => setAlertModal(null)} />
          </div>
        )}
      </Modal>

      {/* DAS-2949: create-Jira-ticket dialog — same flow as the alert cards (DAS-2463) */}
      <Modal
        open={!!jiraAdvice}
        onCancel={() => setJiraAdvice(null)}
        title={jiraAdvice ? `Create Jira ticket — ${(jiraAdvice.action || '').toUpperCase()} ${jiraAdvice.buy_ticker || jiraAdvice.sell_ticker || ''} (${jiraAdvice.run_date})` : ''}
        footer={[
          <Button key="cancel" onClick={() => setJiraAdvice(null)}>Cancel</Button>,
          <Button key="create" type="primary" loading={jiraCreating} onClick={submitJira}
                  data-testid="das2949-jira-submit">
            Create ticket
          </Button>,
        ]}
      >
        {jiraDuplicates === null && <Spin size="small" />}
        {Array.isArray(jiraDuplicates) && jiraDuplicates.length > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 10 }}
            message={`There ${jiraDuplicates.length === 1 ? 'is' : 'are'} already ${jiraDuplicates.length} open ticket(s) about this ticker`}
            description={jiraDuplicates.map(t => (
              <div key={t.key}><a href={t.url} target="_blank" rel="noopener noreferrer">{t.key}</a> {t.summary}</div>
            ))}
          />
        )}
        <Input.TextArea
          rows={3}
          placeholder="Optional notes for the ticket…"
          value={jiraNotes}
          onChange={(e) => setJiraNotes(e.target.value)}
        />
      </Modal>

      <Modal open={analysisOpen} onCancel={() => setAnalysisOpen(false)} footer={null} width={900}
             title="LLM Portfolio Analysis — full reasoning (latest run)">
        {analysisText == null ? <Spin /> : (
          <div style={{ maxHeight: '65vh', overflowY: 'auto', fontSize: 13.5 }}>
            <LLMCostLine cost={analysisCost} style={{ marginBottom: 10 }} />
            <LLMAnalysisBody analysis={analysisText} showTransaction showTimestamp
                             extraTickers={(advice || []).flatMap(a => [a.sell_ticker, a.buy_ticker])} />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default LLMAdviceTab;
